
import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, UserPlus, Users, Mail, CheckCircle } from 'lucide-react';
import { mockJournalists } from '@/components/reader-home/mockData';

interface FriendFinderProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const FriendFinder: React.FC<FriendFinderProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [connectedFriends, setConnectedFriends] = useState<string[]>(formData.connectedFriends || []);
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  
  // Sample friends data (normally would come from an API)
  const sampleFriends = [
    { id: '1', name: 'Alex Johnson', avatar: '/placeholder.svg', email: 'alex@example.com' },
    { id: '2', name: 'Jamie Smith', avatar: '/placeholder.svg', email: 'jamie@example.com' },
    { id: '3', name: 'Taylor Wilson', avatar: '/placeholder.svg', email: 'taylor@example.com' },
    { id: '4', name: 'Morgan Lee', avatar: '/placeholder.svg', email: 'morgan@example.com' },
    { id: '5', name: 'Casey Brown', avatar: '/placeholder.svg', email: 'casey@example.com' },
    { id: '6', name: 'Jordan Adams', avatar: '/placeholder.svg', email: 'jordan@example.com' },
  ];
  
  const filteredFriends = searchTerm 
    ? sampleFriends.filter(friend => 
        friend.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sampleFriends;
  
  const handleConnect = (friendId: string) => {
    if (connectedFriends.includes(friendId)) {
      setConnectedFriends(connectedFriends.filter(id => id !== friendId));
    } else {
      setConnectedFriends([...connectedFriends, friendId]);
    }
  };
  
  const handleInvite = () => {
    if (emailInput && !invitedEmails.includes(emailInput) && emailInput.includes('@')) {
      setInvitedEmails([...invitedEmails, emailInput]);
      setEmailInput('');
    }
  };
  
  const handleNext = () => {
    updateFormData({ connectedFriends, invitedEmails });
    onNext();
  };

  return (
    <OnboardingLayout
      title="Find Friends"
      subtitle="Connect with friends and colleagues on DirectPress"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onPrevious={onPrevious}
      nextLabel="Complete Setup"
    >
      <div className="max-w-3xl mx-auto">
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Find People You Know</h3>
            <div className="flex items-center mb-6 relative">
              <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {filteredFriends.map((friend) => (
                <div 
                  key={friend.id}
                  className="flex items-center p-3 border rounded-lg"
                >
                  <Avatar className="h-10 w-10 mr-3">
                    <AvatarImage src={friend.avatar} alt={friend.name} />
                    <AvatarFallback>{friend.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <p className="font-medium text-sm">{friend.name}</p>
                    <p className="text-xs text-muted-foreground">{friend.email}</p>
                  </div>
                  
                  <Button
                    variant={connectedFriends.includes(friend.id) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleConnect(friend.id)}
                    className="flex items-center gap-1"
                  >
                    {connectedFriends.includes(friend.id) ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        <span>Connected</span>
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-3 w-3" />
                        <span>Connect</span>
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
            
            <h3 className="text-lg font-medium mb-4">Invite by Email</h3>
            <div className="flex gap-2 mb-6">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="friend@example.com" 
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInvite();
                    }
                  }}
                />
              </div>
              <Button onClick={handleInvite} disabled={!emailInput || !emailInput.includes('@')}>
                Invite
              </Button>
            </div>
            
            {invitedEmails.length > 0 && (
              <div className="border rounded-md p-3 mb-6">
                <h4 className="text-sm font-medium mb-2">Invited Friends</h4>
                <div className="space-y-2">
                  {invitedEmails.map((email, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm">{email}</span>
                      <span className="text-xs text-green-600">Invitation sent</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start">
              <Users className="h-10 w-10 text-primary mr-4 mt-1" />
              <div>
                <h3 className="text-lg font-medium mb-2">Growing your network</h3>
                <p className="text-muted-foreground mb-4">
                  Following friends and their journalists helps you discover new content and perspectives.
                  You can always connect with more people after completing the onboarding process.
                </p>
                
                <div className="text-sm text-muted-foreground">
                  <p className="mb-1">Connected with {connectedFriends.length} friends</p>
                  <p>Invited {invitedEmails.length} people by email</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingLayout>
  );
};

export default FriendFinder;
